import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { MdArrowBack, MdContentCopy, MdEdit, MdPlayArrow, MdReplay, MdSchedule, MdSearch, MdSend, MdStop } from 'react-icons/md';
import { getData, postData, putData } from '../../../Webservice/Webservice';
import { DB_FIELDS, FONT, Progress, VariableEditor, WA_GREEN, WA_LOG_STATUS, WaBubble, WaChip, authToken, fillWa, fmtDateTime, pct } from './waShared';

const PAGE_SIZE = 25;
const toLocalInput = (d) => (d ? new Date(new Date(d).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '');

function WhatsappDetails() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [logStatus, setLogStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [hook, setHook] = useState(null); // is Meta delivering status updates?
  const [busy, setBusy] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [edit, setEdit] = useState({ open: false, name: '', when: '', values: {}, errors: {} });

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (logStatus) params.status = logStatus;
      if (search) params.search = search;
      setData(await getData(`whatsapp-campaign/${campaignId}`, params, authToken(), false));
      setError('');
    } catch (e) {
      if (!quiet) setError('Failed to load campaign details.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [campaignId, page, logStatus, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getData('whatsapp-campaign/webhook-status', null, authToken(), false).then(setHook).catch(() => setHook(null));
  }, [data?.campaign?.updatedAt]);
  useEffect(() => {
    getData('whatsapp-campaign/templates', null, authToken(), false).then((r) => setTemplates(r?.templates || [])).catch(() => setTemplates([]));
  }, []);

  const status = data?.campaign?.status;
  // delivered / read ticks keep arriving after the last message, so keep refreshing slowly when finished
  const live = status === 'running' || status === 'pending';
  const watch = status === 'completed';
  useEffect(() => {
    if (!live && !watch) return undefined;
    const t = setInterval(() => load(true), live ? 5000 : 20000);
    return () => clearInterval(t);
  }, [live, watch, load]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const connectWebhook = async () => {
    setBusy(true);
    try {
      await postData('whatsapp-campaign/webhook-subscribe', {}, authToken());
      setHook(await getData('whatsapp-campaign/webhook-status', null, authToken(), false));
    } catch (e) {
      /* the reason is shown by the service */
    } finally {
      setBusy(false);
    }
  };

  const act = async (kind) => {
    setBusy(true);
    try {
      if (kind === 'start') await putData(`whatsapp-campaign/start/${campaignId}`, {}, authToken());
      else if (kind === 'stop') await putData(`whatsapp-campaign/stop/${campaignId}`, {}, authToken());
      else if (kind === 'retry') await putData(`whatsapp-campaign/resend-failed/${campaignId}`, {}, authToken());
      else if (kind === 'duplicate') {
        const res = await postData(`whatsapp-campaign/duplicate/${campaignId}`, {}, authToken());
        if (res?.campaign?._id) return navigate(`/whatsapp/${res.campaign._id}`);
      }
      await load(true);
    } catch (e) {
      /* reason already shown */
    } finally {
      setBusy(false);
      setConfirmStop(false);
    }
  };

  if (loading && !data) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  if (error || !data?.campaign) return <Box sx={{ p: 3 }}><Button startIcon={<MdArrowBack />} onClick={() => navigate('/whatsapp')} sx={{ textTransform: 'none', fontWeight: 700 }}>Back</Button><Alert severity="error" sx={{ mt: 2 }}>{error || 'Campaign not found'}</Alert></Box>;

  const { campaign, logs, meta, breakdown } = data;
  const b = breakdown || {};
  const reached = (b.sent || 0) + (b.delivered || 0) + (b.read || 0);
  const delivered = (b.delivered || 0) + (b.read || 0);
  const total = Object.values(b).reduce((a, n) => a + n, 0);
  const template = templates.find((t) => t._id === campaign.templateName);
  const values = campaign.variableValues || {};
  const canStart = ['draft', 'scheduled', 'pending'].includes(status);
  const canStop = ['draft', 'scheduled', 'pending', 'running'].includes(status);
  const canEdit = ['draft', 'scheduled'].includes(status);

  const cards = [
    { label: 'Recipients', value: total, color: '#6366f1', bg: '#eef2ff' },
    { label: 'Sent to WhatsApp', value: reached, color: '#1d4ed8', bg: '#dbeafe' },
    { label: 'Delivered', value: reached ? `${delivered} (${pct(delivered, reached)}%)` : '-', color: '#059669', bg: '#d1fae5' },
    { label: 'Read', value: reached ? `${b.read || 0} (${pct(b.read || 0, reached)}%)` : '-', color: '#0f766e', bg: '#ccfbf1' },
    { label: 'Failed', value: b.failed || 0, color: '#dc2626', bg: '#fee2e2' },
    { label: 'Waiting', value: b.pending || 0, color: '#b45309', bg: '#fef3c7' },
  ];

  const openEdit = () => setEdit({ open: true, name: campaign.name, when: toLocalInput(campaign.scheduledAt), values: { ...values }, errors: {} });
  const saveEdit = async () => {
    const errors = {};
    if (!edit.name.trim()) errors.name = 'The name is required';
    if (edit.when && new Date(edit.when).getTime() < Date.now()) errors.when = 'That time has already passed';
    const vars = template?.variables || Object.keys(values);
    if (vars.some((v) => !String(edit.values[v] || '').trim())) errors.values = 'Every value needs filling in';
    if (Object.keys(errors).length) return setEdit((e) => ({ ...e, errors }));
    setBusy(true);
    try {
      await putData(`whatsapp-campaign/update/${campaignId}`, { name: edit.name.trim(), scheduledAt: edit.when ? new Date(edit.when).toISOString() : null, variableValues: edit.values }, authToken());
      setEdit((e) => ({ ...e, open: false }));
      await load(true);
    } catch (e) {
      /* shown by the service */
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1250, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <IconButton onClick={() => navigate('/whatsapp')} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}><MdArrowBack size={20} /></IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 23.5, color: '#0f172a', lineHeight: 1.2 }}>{campaign.name}</Typography>
                <WaChip status={status} />
                {live && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#059669', fontFamily: FONT, fontSize: 11.5, fontWeight: 700 }}><CircularProgress size={12} color="inherit" /> live</Box>}
              </Box>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{campaign.campId ? `${campaign.campId} · ` : ''}{campaign.templateName}{hook?.lastEventAt ? ` · last update from Meta ${fmtDateTime(hook.lastEventAt)}` : ''}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canEdit && <Button disabled={busy} onClick={openEdit} startIcon={<MdEdit />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#fefce8', color: '#a16207', '&:hover': { background: '#fef9c3' } }}>Edit</Button>}
            <Button disabled={busy} onClick={() => act('duplicate')} startIcon={<MdContentCopy />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#e0f2fe', color: '#075985', '&:hover': { background: '#bae6fd' } }}>Duplicate</Button>
            {(b.failed || 0) > 0 && status === 'completed' && <Button disabled={busy} onClick={() => act('retry')} startIcon={<MdReplay />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#e0e7ff', color: '#3730a3' }}>Retry {b.failed} failed</Button>}
            {canStart && <Button disabled={busy} onClick={() => act('start')} startIcon={<MdPlayArrow />} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#059669', '&:hover': { background: '#047857' } }}>Start now</Button>}
            {canStop && <Button disabled={busy} onClick={() => setConfirmStop(true)} startIcon={<MdStop />} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', background: '#fee2e2', color: '#b91c1c', '&:hover': { background: '#fecaca' } }}>Stop</Button>}
          </Box>
        </Box>

        {hook?.appSubscribed === false && (
          <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" disabled={busy} onClick={connectWebhook} sx={{ fontWeight: 800, textTransform: 'none' }}>Connect now</Button>}>
            Your app is <b>not subscribed to your WhatsApp Business Account</b>, so Meta sends no delivery or read updates. Press Connect now to fix it.
          </Alert>
        )}
        {/* messages left WhatsApp's door but no delivery update has come back: the webhook is not working */}
        {reached > 0 && delivered === 0 && hook && hook.appSubscribed !== false && !hook.lastEventAt && (Date.now() - new Date(campaign.updatedAt).getTime() > 120000) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Messages were sent, but <b>no delivery update has ever reached this server</b> from Meta. Check the webhook in your Meta app: the callback URL, the verify token, the <b>messages</b> field and the subscription of your app to the WhatsApp Business Account.
          </Alert>
        )}
        {hook?.unmatched24h > 0 && <Alert severity="info" sx={{ mb: 2 }}>{hook.unmatched24h} update(s) from Meta in the last 24 hours did not match any message sent from here (for example messages sent from another tool).</Alert>}
        {status === 'draft' && <Alert severity="info" sx={{ mb: 2 }}>This is a draft. Nothing has been sent. Check the message below, then press <b>Start now</b> or edit it to schedule it.</Alert>}
        {status === 'scheduled' && <Alert severity="info" icon={<MdSchedule />} sx={{ mb: 2 }}>Scheduled for <b>{fmtDateTime(campaign.scheduledAt)}</b>. It goes out by itself at that time.</Alert>}
        {status === 'cancelled' && <Alert severity="error" sx={{ mb: 2 }}>Stopped {fmtDateTime(campaign.stoppedAt)}. The remaining messages were not sent.</Alert>}

        <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
          <Progress stats={{ total, sent: reached, failed: b.failed || 0, skipped: b.cancelled || 0 }} status={status} height={12} />
          <Box sx={{ mt: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {[['Created', fmtDateTime(campaign.createdAt)], ['Scheduled for', campaign.scheduledAt ? fmtDateTime(campaign.scheduledAt) : 'Immediate'], ['Last update', fmtDateTime(campaign.updatedAt)]].map(([k, v]) => (
              <Box key={k}>
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{k}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>{v}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.5, mb: 2.5 }}>
          {cards.map((c) => (
            <Paper key={c.label} elevation={0} sx={{ p: 2, borderRadius: '14px', background: c.bg }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, color: c.color, opacity: 0.85 }}>{c.label}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 20, fontWeight: 800, color: c.color }}>{c.value}</Typography>
            </Paper>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '360px 1fr' }, gap: 2.5, alignItems: 'start' }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1.5 }}>Message</Typography>
            <WaBubble headerType={campaign.headerMediaType !== 'NONE' ? campaign.headerMediaType : undefined} headerImage={campaign.headerMediaType === 'IMAGE' ? campaign.headerMediaUrl : undefined} body={fillWa(template?.content || '', values)} empty="Template text is not available right now." />
            {Object.keys(values).length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                {Object.entries(values).map(([k, v]) => (
                  <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.25 }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: 11.5, color: '#64748b' }}>{`{{${k}}}`}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, textAlign: 'right', wordBreak: 'break-word' }}>{/^\{\{dynamic:/.test(v) ? `from customer: ${v.replace(/^\{\{dynamic:|\}\}$/g, '')}` : v}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5 }}><MdSend style={{ verticalAlign: 'middle', marginRight: 6 }} />Delivery log</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField size="small" placeholder="Search mobile" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} InputProps={{ startAdornment: <MdSearch size={18} style={{ marginRight: 6, color: '#94a3b8' }} /> }} />
                <TextField select size="small" label="Status" value={logStatus} onChange={(e) => { setLogStatus(e.target.value); setPage(1); }} sx={{ minWidth: 130 }}>
                  <MenuItem value="">All</MenuItem>
                  {Object.entries(WA_LOG_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </TextField>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { backgroundColor: '#f8fafc', fontWeight: 800, py: 1.25, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', fontFamily: FONT } }}>
                    <TableCell>#</TableCell><TableCell>Name</TableCell><TableCell>Mobile</TableCell><TableCell>Status</TableCell><TableCell>Sent</TableCell><TableCell>Delivered</TableCell><TableCell>Read</TableCell><TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: '#64748b', fontFamily: FONT }}>No entries.</TableCell></TableRow>
                  ) : logs.map((l, i) => (
                    <TableRow key={l._id} hover sx={{ '& td': { fontFamily: FONT, fontSize: 12 } }}>
                      <TableCell>{(meta.page - 1) * meta.limit + i + 1}</TableCell>
                      <TableCell>{l.customerId?.name || l.externalName || 'N/A'}</TableCell>
                      <TableCell>{l.mobile}</TableCell>
                      <TableCell><WaChip status={l.status} map={WA_LOG_STATUS} /></TableCell>
                      <TableCell>{l.sentAt ? fmtDateTime(l.sentAt) : '-'}</TableCell>
                      <TableCell>{l.deliveredAt ? fmtDateTime(l.deliveredAt) : '-'}</TableCell>
                      <TableCell>{l.readAt ? fmtDateTime(l.readAt) : '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}><Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: l.status === 'failed' ? '#b91c1c' : '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.error}>{l.error || ''}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {meta.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <Pagination count={meta.totalPages} page={meta.page} shape="rounded" showFirstButton showLastButton onChange={(_, p) => setPage(p)} sx={{ '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black' } }} />
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* stop confirmation */}
      <Dialog open={confirmStop} onClose={() => !busy && setConfirmStop(false)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, px: 3, pt: 2 }}>Stop this campaign?</Typography>
        <DialogContent><Typography sx={{ fontFamily: FONT }}>Messages already sent can't be recalled. Everyone still waiting will not receive it, and the campaign can't be restarted.</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmStop(false)} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Keep going</Button>
          <Button onClick={() => act('stop')} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{busy ? 'Stopping…' : 'Yes, stop'}</Button>
        </DialogActions>
      </Dialog>

      {/* edit a draft / scheduled campaign */}
      <Dialog open={edit.open} onClose={() => !busy && setEdit((e) => ({ ...e, open: false }))} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: `linear-gradient(135deg, ${WA_GREEN}1f, ${WA_GREEN}08)`, borderBottom: `3px solid ${WA_GREEN}` }}>
          <Box sx={{ background: WA_GREEN, color: '#fff', borderRadius: '12px', p: 1.1, display: 'flex' }}><MdEdit size={22} /></Box>
          <Box>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, color: '#0f172a', lineHeight: 1.2 }}>Edit campaign</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>Name, schedule and message values can be changed until it starts sending.</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ pt: '24px !important', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField fullWidth size="small" label="Campaign name" value={edit.name} onChange={(e) => setEdit((s) => ({ ...s, name: e.target.value, errors: {} }))} error={!!edit.errors.name} helperText={edit.errors.name} />
          <TextField fullWidth size="small" type="datetime-local" label="Send at" InputLabelProps={{ shrink: true }} value={edit.when} onChange={(e) => setEdit((s) => ({ ...s, when: e.target.value, errors: {} }))} error={!!edit.errors.when} helperText={edit.errors.when || 'Leave empty to keep it as a draft'} />
          {(template?.variables || Object.keys(values)).length > 0 && (
            <Box>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 12.5, mb: 1 }}>Message values</Typography>
              <VariableEditor variables={template?.variables || Object.keys(values)} values={edit.values} onChange={(v) => setEdit((s) => ({ ...s, values: v, errors: {} }))} fields={DB_FIELDS} />
              {edit.errors.values && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#dc2626', mt: 0.75 }}>{edit.errors.values}</Typography>}
            </Box>
          )}
          <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>To change the template, the audience or the header media, duplicate the campaign and create a new one.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setEdit((e) => ({ ...e, open: false }))} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569', borderRadius: '10px', px: 2.5 }}>Cancel</Button>
          <Button onClick={saveEdit} variant="contained" disabled={busy} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3.5, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } }}>{busy ? 'Saving…' : 'Save changes'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WhatsappDetails;
